import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import MessageThread from "./MessageThread";
import { Send, Briefcase, User, MapPin, Loader2 } from "lucide-react";
import { useMutation, useQuery } from "@apollo/client";
import {
  CREATE_CANDIDATE_TOPIC_COMMENT,
  CREATE_JOB_TOPIC_COMMENT,
} from "../../GraphQL/messages/mutations";
import {
  GET_ROLE_TOPIC_COMMENTS,
  GET_CANDIDATE_TOPIC_COMMENTS,
} from "../../GraphQL/messages/queries";
import { cn } from "@/lib/utils";

const TAKE_VALUE: number = 50;

interface IFloatingProps {
  isOpenChat: boolean;
  onHideModal: () => void;
  topic: any;
  userProfile: any;
  client: any;
  isRole: boolean;
}

const FloatingChatMessage = ({
  isOpenChat,
  onHideModal = () => {},
  topic,
  userProfile,
  client,
  isRole,
}: IFloatingProps) => {
  const [messageQuery, setMessageQuery] = useState<string>("");
  const [comments, setComments] = useState([]);
  const [skipVal, setSkipVal] = useState<number>(0);
  const [hasNext, setHasNext] = useState(false);
  const [takeMessage, setTakeMessage] = useState(TAKE_VALUE);
  const [isSent, setIsSent] = useState(false);

  const { loading, refetch, fetchMore } = useQuery(
    isRole ? GET_ROLE_TOPIC_COMMENTS : GET_CANDIDATE_TOPIC_COMMENTS,
    {
      variables: {
        topicId: topic?.id,
        skip: skipVal,
        take: TAKE_VALUE,
      },
      onCompleted: (data) => {
        const commentsLength = isRole
          ? data.jobTopicComments.items.length
          : data.candidateTopicComments.items.length;
        if (commentsLength > 0) {
          setComments(
            isRole
              ? data.jobTopicComments.items
              : data.candidateTopicComments.items
          );
        }
        setHasNext(
          isRole
            ? data?.jobTopicComments?.pageInfo?.hasNextPage
            : data?.candidateTopicComments?.pageInfo?.hasNextPage
        );
      },
      fetchPolicy: "network-only",
      nextFetchPolicy: "cache-first",
    }
  );

  const [createChatMessage, { loading: loadingCreateChat }] = useMutation(
    isRole ? CREATE_JOB_TOPIC_COMMENT : CREATE_CANDIDATE_TOPIC_COMMENT,
    {
      onCompleted() {
        let takeValue = takeMessage;
        if (takeMessage >= 60) takeValue = 50;
        refetch({ topicId: topic?.id, skip: 0, take: takeValue });
        setMessageQuery("");
        setIsSent(true);
      },
    }
  );

  useEffect(() => {
    setSkipVal(0);
    setTakeMessage(0);
  }, [topic?.id]);

  const handleSendMessage = () => {
    if (messageQuery === "") {
      toast.error("Message is empty");
      return;
    } else {
      createChatMessage({
        variables: { topicId: `${topic?.id}`, message: messageQuery },
      });
      setTakeMessage(takeMessage + TAKE_VALUE);
    }
  };

  const handleLoadMore = () => {
    setSkipVal((val) => val + TAKE_VALUE);
    const updateData = async () => {
      const { data } = await fetchMore({
        variables: {
          topicId: `${topic?.id}`,
          skip: skipVal + TAKE_VALUE,
          take: TAKE_VALUE,
        },
      });
      const newEl: [] = isRole
        ? data?.jobTopicComments?.items
        : data?.candidateTopicComments?.items;
      setComments([...comments, ...newEl]);
      setTakeMessage(takeMessage + TAKE_VALUE);
    };
    updateData();
  };

  const title = isRole ? topic?.jobTitle : topic?.idealJobTitles;

  return (
    <Dialog open={isOpenChat} onOpenChange={onHideModal}>
      <DialogContent
        className={cn(
          // Size and positioning
          "max-w-3xl w-full max-h-[90vh]",
          // Frosted glass aesthetic
          "bg-white/95 dark:bg-slate-900/95",
          "backdrop-blur-xl",
          // Border and shadow
          "border border-slate-200/60 dark:border-slate-700/50",
          "shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)]",
          "dark:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)]",
          // Animation - scale in
          "data-[state=open]:animate-in",
          "data-[state=open]:fade-in-0",
          "data-[state=open]:zoom-in-95",
          "data-[state=open]:duration-300",
          "data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0",
          "data-[state=closed]:zoom-out-95",
          "data-[state=closed]:duration-200",
          // Overflow handling
          "flex flex-col"
        )}
      >
        {/* Header */}
        <DialogHeader
          className={cn(
            "pb-4 border-b border-slate-200/60 dark:border-slate-700/50",
            "shrink-0"
          )}
        >
          {/* Type badge */}
          <div className="flex items-center gap-2 mb-3">
            <Badge
              variant="outline"
              className={cn(
                "font-mono text-[10px] uppercase tracking-wider",
                "border-slate-300 dark:border-slate-600",
                "text-slate-600 dark:text-slate-300",
                "bg-slate-50/50 dark:bg-slate-800/50"
              )}
            >
              {isRole ? (
                <>
                  <Briefcase className="w-3 h-3 mr-1.5" />
                  Role Query
                </>
              ) : (
                <>
                  <User className="w-3 h-3 mr-1.5" />
                  Candidate Query
                </>
              )}
            </Badge>
          </div>

          {/* Title - Editorial typography */}
          <DialogTitle
            className={cn(
              "font-serif text-2xl font-semibold leading-tight",
              "text-slate-900 dark:text-slate-50",
              "tracking-tight"
            )}
          >
            {title}
          </DialogTitle>

          {/* Location metadata */}
          {topic?.locationDisplayName && (
            <div
              className={cn(
                "flex items-center gap-1.5 mt-2",
                "text-muted-foreground"
              )}
            >
              <MapPin className="w-3.5 h-3.5" />
              <span className="font-mono text-[12px]">
                {topic?.locationDisplayName}
              </span>
            </div>
          )}
        </DialogHeader>

        {/* Message Thread */}
        <div className="flex-1 min-h-0 py-4">
          <MessageThread
            topic={topic}
            userProfile={userProfile}
            comments={comments}
            onLoadMore={handleLoadMore}
            hasNext={hasNext}
            onLoading={loading}
            isSent={isSent}
          />
        </div>

        {/* Footer with input */}
        <DialogFooter
          className={cn(
            "pt-4 border-t border-slate-200/60 dark:border-slate-700/50",
            "flex-col gap-3 shrink-0",
            "sm:flex-col sm:space-x-0"
          )}
        >
          {/* Textarea with enhanced styling */}
          <Textarea
            placeholder="Type your message..."
            onChange={(e) => setMessageQuery(e.target.value)}
            value={messageQuery}
            rows={3}
            className={cn(
              "w-full resize-none",
              "bg-slate-50/50 dark:bg-slate-800/50",
              "border-slate-200 dark:border-slate-700",
              "placeholder:text-slate-400 dark:placeholder:text-slate-500",
              // Focus states
              "focus:ring-2 focus:ring-primary/20",
              "focus:border-primary dark:focus:border-teal-400",
              // Typography
              "font-sans text-[15px] leading-relaxed",
              // Transitions
              "transition-all duration-200"
            )}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.metaKey) {
                handleSendMessage();
              }
            }}
          />

          {/* Action row */}
          <div className="flex items-center justify-between w-full">
            {/* Keyboard hint */}
            <span className="font-mono text-[10px] text-muted-foreground hidden sm:block">
              Press <kbd className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-[10px] border border-slate-200 dark:border-slate-700">⌘</kbd> + <kbd className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-[10px] border border-slate-200 dark:border-slate-700">Enter</kbd> to send
            </span>

            {/* Send button */}
            <Button
              onClick={handleSendMessage}
              disabled={loadingCreateChat || !messageQuery.trim()}
              className={cn(
                "min-w-[140px] h-10",
                "bg-primary hover:bg-primary/90",
                "dark:bg-teal-600 dark:hover:bg-teal-500",
                // Press effect
                "transition-all duration-150",
                "active:scale-[0.97]",
                "motion-reduce:active:scale-100",
                // Disabled state
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              {loadingCreateChat ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FloatingChatMessage;
